import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';

import type {
  DetailedCircuitFeature,
  DetailedCircuitGeometry,
  TrackContributionGpsCoord,
} from '@kangaroo/shared';

import { getGoogleMapsApiKey } from '../../lib/runtimeConfig';
import {
  loadGoogleMaps,
  mapsDevAuthHint,
  mapsMissingKeyHint,
  onMapsAuthFailure,
  registerGoogleMapsAuthFailureHandler,
} from '../../lib/mapsLoader';
import {
  trackCatalogSegmentColor,
  trackCatalogSegmentVisible,
  type TrackCatalogMapLayers,
  type TrackCatalogSegmentLine,
  type TrackCatalogTrackConfig,
  type TrackCatalogV2TrackConfig,
} from '../../lib/trackCatalog';
import { buildDetailedCircuitGeometry } from '../../lib/trackCatalogContribution';

type CatalogProps = {
  config: TrackCatalogTrackConfig;
  geometry: TrackCatalogV2TrackConfig;
  layers: TrackCatalogMapLayers;
  selectedSegmentUuid: string | null;
  onSelectSegment: (segment: TrackCatalogSegmentLine | null) => void;
  kerbEditMode?: 'none' | 'draw' | 'erase';
  kerbLines?: TrackContributionGpsCoord[][];
  onKerbStroke?: (points: TrackContributionGpsCoord[], complete: boolean) => void;
  onKerbErase?: (point: TrackContributionGpsCoord) => void;
  minHeight?: number | string;
};

type DetailedProps = {
  geometry: DetailedCircuitGeometry;
  geometryKey: string;
  layers: TrackCatalogMapLayers;
  selectedFeatureId?: string | null;
  onSelectFeature?: (feature: DetailedCircuitFeature | null) => void;
  kerbEditMode?: 'none' | 'draw' | 'erase';
  kerbLines?: TrackContributionGpsCoord[][];
  onKerbStroke?: (points: TrackContributionGpsCoord[], complete: boolean) => void;
  onKerbErase?: (point: TrackContributionGpsCoord) => void;
  minHeight?: number | string;
};

type MapOverlay = google.maps.Polygon | google.maps.Polyline | google.maps.Marker;

function googlePaths(paths: TrackContributionGpsCoord[][]) {
  return paths.map((path) => path.map(({ lat, lon }) => ({ lat, lng: lon })));
}

function clearOverlays(overlays: MapOverlay[]) {
  for (const overlay of overlays) {
    overlay.setMap(null);
  }
}

function labelText(feature: DetailedCircuitFeature) {
  if (feature.type.toLowerCase() === 'corner') {
    const number = feature.label.match(/\d+/)?.[0];
    return number ? `T${number}` : feature.label;
  }
  if (feature.type.toLowerCase() === 'sector') {
    const number = feature.label.match(/\d+/)?.[0];
    return number ? `S${number}` : feature.label;
  }
  if (feature.type.toLowerCase() === 'startfinish') {
    return 'S/F';
  }
  if (feature.type.toLowerCase() === 'kerbmarker') {
    return feature.label.replace(/^Kerb\s*/i, 'K');
  }
  if (feature.type.toLowerCase() === 'gridmarker') {
    return 'GRID';
  }
  if (feature.type.toLowerCase() === 'distancemarker') {
    return feature.label;
  }
  return '';
}

function pathCenter(paths: TrackContributionGpsCoord[][]) {
  const points = paths.flat();
  if (points.length === 0) {
    return null;
  }
  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lng: points.reduce((sum, point) => sum + point.lon, 0) / points.length,
  };
}

export const DETAILED_CIRCUIT_DEFAULT_LAYERS: TrackCatalogMapLayers = {
  trackOutline: true,
  corners: true,
  straights: true,
  sectors: true,
  lines: true,
  labels: true,
};

export function DetailedCircuitMap({
  geometry,
  layers,
  geometryKey,
  selectedFeatureId = null,
  onSelectFeature = () => undefined,
  kerbEditMode = 'none',
  kerbLines = [],
  onKerbStroke = () => undefined,
  onKerbErase = () => undefined,
  minHeight = 560,
}: DetailedProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const overlaysRef = useRef<MapOverlay[]>([]);
  const fittedConfigRef = useRef<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) {
      setError(mapsMissingKeyHint());
      return;
    }

    let cancelled = false;
    registerGoogleMapsAuthFailureHandler();
    const removeAuthListener = onMapsAuthFailure(() => {
      if (!cancelled) {
        setError(mapsDevAuthHint(apiKey.slice(-6)));
      }
    });

    loadGoogleMaps(apiKey, { includePlaces: false })
      .then(() => {
        if (cancelled || !containerRef.current) {
          return;
        }
        mapRef.current = new google.maps.Map(containerRef.current, {
          center: { lat: 0, lng: 0 },
          zoom: 3,
          mapTypeId: google.maps.MapTypeId.SATELLITE,
          tilt: 0,
          fullscreenControl: true,
          mapTypeControl: true,
          streetViewControl: false,
          clickableIcons: false,
          gestureHandling: 'greedy',
        });
        setMapReady(true);
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Google Maps could not be loaded.');
        }
      });

    return () => {
      cancelled = true;
      removeAuthListener();
      clearOverlays(overlaysRef.current);
      overlaysRef.current = [];
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) {
      return;
    }

    clearOverlays(overlaysRef.current);
    const overlays: MapOverlay[] = [];
    const bounds = new google.maps.LatLngBounds();

    const addToBounds = (paths: TrackContributionGpsCoord[][]) => {
      for (const path of paths) {
        for (const point of path) {
          bounds.extend({ lat: point.lat, lng: point.lon });
        }
      }
    };

    if (layers.trackOutline) {
      const paths = geometry.outlineRings;
      if (paths.length > 0) {
        addToBounds(paths);
        const outline = new google.maps.Polygon({
          map,
          paths: googlePaths(paths),
          fillColor: '#ffffff',
          fillOpacity: 0.04,
          strokeColor: '#ffffff',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          clickable: false,
          zIndex: 0,
        });
        overlays.push(outline);
      }
    }

    if (geometry.centerLine && geometry.centerLine.length > 1) {
      addToBounds([geometry.centerLine]);
      overlays.push(new google.maps.Polyline({
        map,
        path: googlePaths([geometry.centerLine])[0],
        strokeColor: '#28c8ff',
        strokeOpacity: 0.9,
        strokeWeight: 3,
        clickable: false,
        zIndex: 2,
      }));
    }

    const orderedFeatures = [...geometry.features].sort((left, right) => {
      const rank = (type: string) => type.toLowerCase() === 'sector' ? 0 : 1;
      return rank(left.type) - rank(right.type);
    });

    for (const feature of orderedFeatures) {
      if (!trackCatalogSegmentVisible(feature.type, layers)) {
        continue;
      }
      const paths = feature.polygonRings ?? [];
      if (paths.length === 0) {
        continue;
      }

      addToBounds(paths);
      const color = trackCatalogSegmentColor(feature.type);
      const isSector = feature.type.toLowerCase() === 'sector';
      const selected = feature.id === selectedFeatureId;
      const polygon = new google.maps.Polygon({
        map,
        paths: googlePaths(paths),
        fillColor: color,
        fillOpacity: isSector ? 0.11 : selected ? 0.58 : 0.34,
        strokeColor: selected ? '#ffffff' : color,
        strokeOpacity: isSector ? 0.6 : 0.95,
        strokeWeight: selected ? 4 : isSector ? 1.5 : 2,
        clickable: kerbEditMode === 'none',
        zIndex: isSector ? 1 : selected ? 5 : 3,
      });
      if (kerbEditMode === 'none') {
        polygon.addListener('click', () => {
          onSelectFeature(feature);
        });
      }
      overlays.push(polygon);
    }

    if (layers.lines) {
      for (const feature of geometry.features) {
        if (!trackCatalogSegmentVisible(feature.type, layers)) {
          continue;
        }
        const color = trackCatalogSegmentColor(feature.type);
        const isStartFinish = feature.type.toLowerCase() === 'startfinish';
        const lineShapes = [feature.entryLine, feature.exitLine];
        const seen = new Set<string>();
        for (const path of lineShapes) {
          if (!path || path.length < 2) {
            continue;
          }
          const key = JSON.stringify(path);
          if (seen.has(key)) {
            continue;
          }
          seen.add(key);
          addToBounds([path]);
          const line = new google.maps.Polyline({
            map,
            path: googlePaths([path])[0],
            strokeColor: isStartFinish ? '#ffe66d' : color,
            strokeOpacity: isStartFinish ? 1 : 0.82,
            strokeWeight: isStartFinish ? 6 : 2,
            clickable: kerbEditMode === 'none',
            zIndex: isStartFinish ? 9 : 7,
          });
          if (kerbEditMode === 'none') {
            line.addListener('click', () => onSelectFeature(feature));
          }
          overlays.push(line);
        }
      }
    }

    if (layers.labels) {
      for (const feature of geometry.features) {
        if (!trackCatalogSegmentVisible(feature.type, layers)) {
          continue;
        }
        const text = labelText(feature);
        if (!text) {
          continue;
        }
        const labelPoint = feature.apexPoint ?? feature.directionalPoint;
        const point = labelPoint
          ? { lat: labelPoint.lat, lng: labelPoint.lon }
          : pathCenter(feature.polygonRings ?? []);
        if (!point) {
          continue;
        }
        const marker = new google.maps.Marker({
          map,
          position: point,
          title: feature.label,
          label: {
            text,
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: '800',
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: trackCatalogSegmentColor(feature.type),
            fillOpacity: 0.94,
            strokeColor: '#07111c',
            strokeWeight: 2,
            scale: 12,
          },
          zIndex: 12,
          clickable: kerbEditMode === 'none',
        });
        if (kerbEditMode === 'none') {
          marker.addListener('click', () => onSelectFeature(feature));
        }
        overlays.push(marker);
      }
    }

    overlaysRef.current = overlays;
    if (!bounds.isEmpty() && fittedConfigRef.current !== geometryKey) {
      map.fitBounds(bounds, 48);
      fittedConfigRef.current = geometryKey;
      google.maps.event.addListenerOnce(map, 'idle', () => {
        if ((map.getZoom() ?? 0) > 20) {
          map.setZoom(20);
        }
      });
    }

    return () => {
      clearOverlays(overlays);
      if (overlaysRef.current === overlays) {
        overlaysRef.current = [];
      }
    };
  }, [geometry, geometryKey, kerbEditMode, layers, mapReady, onSelectFeature, selectedFeatureId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || kerbEditMode === 'none') return;
    let drawing = false;
    let points: TrackContributionGpsCoord[] = [];
    map.setOptions({ draggable: false, gestureHandling: 'none', disableDoubleClickZoom: true });
    const down = map.addListener('mousedown', (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;
      const point = { lat: event.latLng.lat(), lon: event.latLng.lng() };
      if (kerbEditMode === 'erase') {
        drawing = true;
        onKerbErase(point);
        return;
      }
      drawing = true;
      points = [point];
    });
    const move = map.addListener('mousemove', (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;
      const point = { lat: event.latLng.lat(), lon: event.latLng.lng() };
      if (kerbEditMode === 'erase') {
        if (drawing) onKerbErase(point);
        return;
      }
      if (!drawing) return;
      points.push(point);
      onKerbStroke(points, false);
    });
    const up = map.addListener('mouseup', (event: google.maps.MapMouseEvent) => {
      if (!drawing) return;
      if (kerbEditMode === 'erase') {
        drawing = false;
        return;
      }
      if (event.latLng) points.push({ lat: event.latLng.lat(), lon: event.latLng.lng() });
      drawing = false;
      if (points.length >= 2) onKerbStroke(points, true);
      points = [];
    });
    return () => {
      down.remove(); move.remove(); up.remove();
      map.setOptions({ draggable: true, gestureHandling: 'greedy', disableDoubleClickZoom: false });
    };
  }, [kerbEditMode, mapReady, onKerbErase, onKerbStroke]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const overlays = kerbLines.filter((line) => line.length >= 2).map((line) => new google.maps.Polyline({
      map,
      path: googlePaths([line])[0],
      strokeColor: '#f97316',
      strokeOpacity: 1,
      strokeWeight: 7,
      clickable: false,
      zIndex: 20,
    }));
    return () => clearOverlays(overlays);
  }, [kerbLines, mapReady]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', minHeight, overflow: 'hidden', borderRadius: 2 }}>
      <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }} />
      {!mapReady ? (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(7,17,28,0.72)' }}>
          <CircularProgress size={34} />
        </Box>
      ) : null}
    </Box>
  );
}

export function TrackCatalogMap({
  config,
  geometry,
  layers,
  selectedSegmentUuid,
  onSelectSegment,
  kerbEditMode,
  kerbLines,
  onKerbStroke,
  onKerbErase,
  minHeight = 560,
}: CatalogProps) {
  const detailedGeometry = useMemo(
    () => buildDetailedCircuitGeometry(config, geometry),
    [config, geometry],
  );
  const segmentById = useMemo(
    () => new Map(config.segmentLines.map((segment) => [segment.uuid, segment])),
    [config.segmentLines],
  );
  const handleSelectFeature = useCallback(
    (feature: DetailedCircuitFeature | null) => {
      onSelectSegment(feature ? segmentById.get(feature.id) ?? null : null);
    },
    [onSelectSegment, segmentById],
  );

  return (
    <DetailedCircuitMap
      geometry={detailedGeometry}
      geometryKey={config.uuid}
      layers={layers}
      selectedFeatureId={selectedSegmentUuid}
      onSelectFeature={handleSelectFeature}
      kerbEditMode={kerbEditMode}
      kerbLines={kerbLines}
      onKerbStroke={onKerbStroke}
      onKerbErase={onKerbErase}
      minHeight={minHeight}
    />
  );
}
